// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Retiring a machine (design doc §8.7, US-S-02).
 *
 * Offered from the archive list, where someone is looking at a fleet and deciding which of
 * them is still in service — not from the record of one machine, which is a page people
 * open to read.
 *
 * The consequence is stated in the dialog rather than left to be discovered: scrapping
 * releases every serial this machine holds, so those parts can be filed against another
 * one (§8.7 step 5). That is the part an operator cannot undo and would not otherwise see,
 * and it is why the reason is required — a released serial with no explanation of why it
 * was released is the gap the audit trail exists to close.
 *
 * **Modules** (component design §9.7). Every module fitted in it is listed, a level at a
 * time, each with where it goes: back into stock — the default — or scrapped with it. A
 * module going back to stock keeps what is fitted in it, so its own modules are not asked
 * about; one scrapped asks the same question of each module inside it.
 *
 * **Parts** (§8.7). Every part with a serial that goes with it — on the machine itself, and
 * in each module scrapped with it — is listed with where it goes: back into stock (the
 * default) or scrapped. Every one is recorded either way, so where each serial went is
 * never a question. The parts of a module going back to stock stay in it and are not
 * listed; switching a module to scrapped lists them.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/endpoints";
import type { ArchiveItem, ArchiveSummary, PartFate } from "@/api/types";
import { Modal } from "./Modal";

export type ModuleFate = "RELEASE" | "SCRAP";

interface Props {
  archive: ArchiveSummary;
  pending: boolean;
  error: React.ReactNode;
  /** `modules`: each fitted module's fate by archive id; any not named goes to stock.
   *  `parts`: each listed part's fate by position id. */
  onScrap: (
    reason: string,
    modules: Record<string, ModuleFate>,
    parts: Record<string, PartFate>,
  ) => void;
  onClose: () => void;
}

export function ScrapDialog({ archive, pending, error, onScrap, onClose }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [fates, setFates] = useState<Record<string, ModuleFate>>({});
  const [partFates, setPartFates] = useState<Record<string, PartFate>>({});
  const detail = useQuery({
    queryKey: ["archive", archive.id, "expanded"],
    queryFn: () => api.archives.get(archive.id, true),
  });
  const slots = (detail.data?.items ?? []).filter((item) => item.module);
  const groups = partGroups(archive.archive_no, detail.data?.items ?? [], fates);
  const parts = groups.flatMap((group) => group.parts);
  // Only what is listed now: a part of a module switched back to stock goes with it.
  const chosenParts = () =>
    Object.fromEntries(
      parts.map((part) => [part.position_id, partFates[part.position_id] ?? "RESTOCKED"]),
    );
  const setAll = (fate: PartFate) =>
    setPartFates(Object.fromEntries(parts.map((part) => [part.position_id, fate])));

  return (
    <Modal
      title={t("archive.scrap")}
      onClose={onClose}
      onSubmit={() => onScrap(reason.trim(), fates, chosenParts())}
      footer={
        <>
          <button type="submit" className="button-danger" disabled={!reason.trim() || pending}>
            {t("archive.scrapConfirmButton")}
          </button>
          <button type="button" onClick={onClose}>
            {t("common.cancel")}
          </button>
        </>
      }
    >
      <p>
        <span className="archive-no">{archive.archive_no}</span>{" "}
        <span className="mono muted">{archive.device_no ?? archive.module_sn ?? "—"}</span>
      </p>
      <div className="banner banner-error">{t("archive.scrapConfirm")}</div>
      <div className="form-row">
        <label className="required" htmlFor="scrap-reason">
          {t("archive.scrapReason")}
        </label>
        <input
          id="scrap-reason"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
      {slots.length > 0 && (
        <fieldset className="scrap-modules">
          <legend>{t("scrap.modulesTitle")}</legend>
          <ModuleFates slots={slots} fates={fates} onChange={setFates} level={0} />
        </fieldset>
      )}
      {parts.length > 0 && (
        <fieldset className="scrap-parts">
          <legend>{t("scrap.partsTitle", { n: parts.length })}</legend>
          <p className="hint">{t("scrap.partsHint")}</p>
          <div className="filters filters-inline">
            <button type="button" onClick={() => setAll("RESTOCKED")}>
              {t("scrap.allRestocked")}
            </button>
            <button type="button" onClick={() => setAll("SCRAPPED")}>
              {t("scrap.allScrapped")}
            </button>
          </div>
          <table className="scrap-part-list">
            <thead>
              <tr>
                <th>{t("archive.position")}</th>
                <th>{t("archive.materialCode")}</th>
                <th>{t("archive.serial")}</th>
                <th>{t("scrap.partFate")}</th>
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody key={group.label}>
                {groups.length > 1 && (
                  <tr className="scrap-part-group">
                    <th colSpan={4} className="mono">
                      {group.label}
                    </th>
                  </tr>
                )}
                {group.parts.map((part) => (
                  <tr key={part.position_id}>
                    <td>{part.position_name}</td>
                    <td className="mono">{part.material_code ?? "—"}</td>
                    <td className="mono">{part.serial_raw ?? part.serial_normalized}</td>
                    <td>
                      <select
                        aria-label={t("scrap.partFateOf", { position: part.position_name })}
                        value={partFates[part.position_id] ?? "RESTOCKED"}
                        onChange={(event) =>
                          setPartFates({
                            ...partFates,
                            [part.position_id]: event.target.value as PartFate,
                          })
                        }
                      >
                        <option value="RESTOCKED">{t("scrap.restocked")}</option>
                        <option value="SCRAPPED">{t("scrap.scrapped")}</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </fieldset>
      )}
      {error}
    </Modal>
  );
}

function ModuleFates({
  slots,
  fates,
  onChange,
  level,
}: {
  slots: ArchiveItem[];
  fates: Record<string, ModuleFate>;
  onChange: (fates: Record<string, ModuleFate>) => void;
  level: number;
}) {
  const { t } = useTranslation();
  return (
    <ul className="scrap-module-list" style={{ paddingLeft: level ? 20 : 0 }}>
      {slots.map((slot) => {
        const module = slot.module;
        if (!module) return null;
        const fate = fates[module.archive_id] ?? "RELEASE";
        const inner = (slot.children ?? []).filter((child) => child.module);
        return (
          <li key={slot.position_id}>
            {slot.position_name} <span className="mono">{module.archive_no}</span>{" "}
            <span className="module-badge">{module.module_type}</span>
            <select
              aria-label={t("scrap.fateOf", { archiveNo: module.archive_no })}
              value={fate}
              onChange={(event) =>
                onChange({ ...fates, [module.archive_id]: event.target.value as ModuleFate })
              }
            >
              <option value="RELEASE">{t("scrap.release")}</option>
              <option value="SCRAP">{t("scrap.scrapWith")}</option>
            </select>
            {inner.length > 0 &&
              (fate === "SCRAP" ? (
                <ModuleFates slots={inner} fates={fates} onChange={onChange} level={level + 1} />
              ) : (
                <p className="hint">{t("scrap.staysInside", { n: inner.length })}</p>
              ))}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The serialized parts that go with the archive, grouped by the archive they are on: the
 * machine's own, then each module scrapped with it, at any depth. A module slot is not a
 * part; a module going back to stock keeps its parts.
 */
function partGroups(
  label: string,
  items: ArchiveItem[],
  fates: Record<string, ModuleFate>,
): { label: string; parts: ArchiveItem[] }[] {
  const own = items.filter(
    (item) => !item.module && item.origin !== "BOM_MODULE" && item.serial_normalized,
  );
  const inner = items
    .filter((item) => item.module && fates[item.module.archive_id] === "SCRAP")
    .flatMap((item) =>
      partGroups(
        `${item.position_name} ${item.module?.archive_no ?? ""}`.trim(),
        item.children ?? [],
        fates,
      ),
    );
  return [...(own.length > 0 ? [{ label, parts: own }] : []), ...inner];
}
